import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UnifiedContactModal } from "@/components/contacts/UnifiedContactModal";
import { LinkedInFollowUpPanel } from "@/components/contacts/panels/LinkedInFollowUpPanel";
import type { CampaignOutreachContact } from "@/types/outreach";

interface DraftOutreachLauncherProps {
  accountId: string | null;
  accountName?: string | null;
  accountDomain?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DraftOutreachLauncher({
  accountId,
  accountName,
  accountDomain,
  open,
  onOpenChange,
}: DraftOutreachLauncherProps) {
  const { data: topContact, isLoading } = useQuery({
    queryKey: ["account-top-contact", accountId],
    queryFn: async () => {
      if (!accountId) return null;
      const { data } = await supabase
        .from("contacts")
        .select(
          "id, first_name, last_name, full_name, job_title, company_id, email, linkedin_url, headline, seniority_level, department, location, marketing_persona, is_outreach_target",
        )
        .eq("company_id", accountId)
        .eq("is_outreach_target", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!data) return null;
      const c = data as Record<string, any>;
      const draft: CampaignOutreachContact = {
        contact_id: c.id,
        first_name: c.first_name,
        last_name: c.last_name,
        full_name: c.full_name,
        job_title: c.job_title,
        company_name: accountName || null,
        company_id: accountId,
        account_domain: accountDomain || null,
        email: c.email,
        linkedin_url: c.linkedin_url,
        headline: c.headline,
        seniority_level: c.seniority_level,
        department: c.department,
        location: c.location,
      };
      return draft;
    },
    enabled: !!accountId && open,
    staleTime: 5 * 60 * 1000,
  });

  if (!open) return null;

  if (!isLoading && !topContact) {
    if (typeof window !== "undefined") {
      import("sonner").then(({ toast }) =>
        toast.error("No outreach-target contact on this account"),
      );
    }
    onOpenChange(false);
    return null;
  }

  return (
    <UnifiedContactModal
      contactId={topContact?.contact_id ?? null}
      open={open}
      onOpenChange={onOpenChange}
      mode="linkedin"
      panel={topContact?.contact_id ? <LinkedInFollowUpPanel contactId={topContact.contact_id} /> : undefined}
    />
  );
}
